/**
 * AIReviewTabHelpers — sub-components for AIReviewTab (components only, so
 * Vite Fast Refresh works correctly). Non-component utilities live in
 * reviewOutputTypes.ts and buildReviewPrompt.ts.
 */
import React, { useState } from 'react';
import { C, RATING_SCALE, getRatingColor } from '../../data/managerReviewData';

// ── Review quality display — two-tier (blockers / suggestions) ───────────────

interface ParsedIssue {
  tier: 'BLOCKER' | 'SUGGESTION';
  category: string;
  action: string;
  location: string | null;
}

// Categories that map to BLOCKER tier (used for old cached format)
const BLOCKER_CATS = new Set(['INCONSISTENCY', 'BIAS', 'INCOMPLETE']);

function parseQualityIssues(body: string): ParsedIssue[] {
  return body.split('\n').flatMap(line => {
    const trimmed = line.trim();
    if (!trimmed) return [];

    // Current format (no brackets): BLOCKER INCONSISTENCY — action → location
    const nobracket = trimmed.match(/^(BLOCKER|SUGGESTION)\s+([A-Z][A-Z_\s]*?)\s*[–—-]\s*(.+)/i);
    if (nobracket) {
      const tier = nobracket[1].toUpperCase() as 'BLOCKER' | 'SUGGESTION';
      const category = nobracket[2].trim().toUpperCase().replace(/_/g, ' ');
      const rest = nobreak(nobracket[3].trim());
      const locMatch = rest.match(/^(.*?)\s*→\s*(.+)$/);
      return [{ tier, category, action: locMatch ? locMatch[1].trim() : rest, location: locMatch ? locMatch[2].trim() : null }];
    }

    // Legacy bracket format: [BLOCKER] CATEGORY — action → location
    const bracketNew = trimmed.match(/^\[(BLOCKER|SUGGESTION)\]\s+([A-Z][A-Z_\s]*?)\s*[–—-]\s*(.+)/i);
    if (bracketNew) {
      const tier = bracketNew[1].toUpperCase() as 'BLOCKER' | 'SUGGESTION';
      const category = bracketNew[2].trim().toUpperCase().replace(/_/g, ' ');
      const rest = nobreak(bracketNew[3].trim());
      const locMatch = rest.match(/^(.*?)\s*→\s*(.+)$/);
      return [{ tier, category, action: locMatch ? locMatch[1].trim() : rest, location: locMatch ? locMatch[2].trim() : null }];
    }

    // Legacy bracket format (oldest): [CATEGORY] — action (no tier prefix, infer from category)
    if (trimmed.startsWith('[')) {
      const bracketOld = trimmed.match(/^\[([A-Z][A-Z_\s]*?)\]\s*(?:[–—-]\s*)?(.+)/i);
      if (bracketOld) {
        const category = bracketOld[1].trim().toUpperCase().replace(/_/g, ' ');
        const rest = nobreak(bracketOld[2].trim());
        const locMatch = rest.match(/^(.*?)\s*→\s*(.+)$/);
        const tier: 'BLOCKER' | 'SUGGESTION' = BLOCKER_CATS.has(category) ? 'BLOCKER' : 'SUGGESTION';
        return [{ tier, category, action: locMatch ? locMatch[1].trim() : rest, location: locMatch ? locMatch[2].trim() : null }];
      }
    }

    return [];
  });
}

// Trim trailing period from action text (model often adds one)
function nobreak(s: string) { return s.replace(/\.\s*$/, ''); }

const slugify = (s: string) => s.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

function resolveLocation(location: string): { tab?: number; anchor?: string; sidebar?: 'feedback' } {
  const loc = location.trim();
  if (loc === 'Summary') return { tab: 2 };
  if (loc === 'Before You Start') return { sidebar: 'feedback' };
  if (loc.startsWith('Delivery: ')) return { tab: 0, anchor: `deliverable-${slugify(loc.slice(10))}` };
  if (loc.startsWith('Behaviors: ')) {
    const val = loc.slice(11).trim();
    return { tab: 1, anchor: val === 'Summary' ? 'behavior-summary' : `behavior-${slugify(val)}` };
  }
  return {};
}

function IssueRow({ issue, onNavigate, onOpenFeedback }: {
  issue: ParsedIssue;
  onNavigate?: (tab: number, anchor?: string) => void;
  onOpenFeedback?: () => void;
}) {
  const nav = issue.location ? resolveLocation(issue.location) : null;
  const canNavigate = !!(nav && (nav.tab !== undefined || nav.sidebar) && (onNavigate || onOpenFeedback));

  const handleClick = () => {
    if (!nav || !canNavigate) return;
    if (nav.sidebar && onOpenFeedback) { onOpenFeedback(); return; }
    if (nav.tab !== undefined && onNavigate) {
      onNavigate(nav.tab, nav.anchor);
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: 10,
      padding: '9px 14px',
      borderBottom: '1px solid #f3f4f6',
    }}>
      <span style={{
        fontSize: 9, fontWeight: 700, color: '#6b7280',
        background: '#f3f4f6', borderRadius: 3, padding: '2px 5px',
        letterSpacing: '0.06em', whiteSpace: 'nowrap', flexShrink: 0,
        textTransform: 'uppercase', width: 112, display: 'inline-block',
      }}>{issue.category}</span>
      <span style={{ fontSize: 13, color: '#1a1a1a', lineHeight: 1.55, flex: 1 }}>
        {issue.action}
      </span>
      {issue.location && (
        canNavigate ? (
          <button
            onClick={handleClick}
            style={{
              fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap', flexShrink: 0,
              fontStyle: 'italic', background: 'none', border: 'none', cursor: 'pointer',
              padding: 0, fontFamily: 'Inter', textDecoration: 'underline',
              textDecorationStyle: 'dotted', textUnderlineOffset: '2px',
            }}
          >→ {issue.location}</button>
        ) : (
          <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap', flexShrink: 0, fontStyle: 'italic' }}>
            → {issue.location}
          </span>
        )
      )}
    </div>
  );
}

export function QualityDisplay({ raw, hasInsufficientData, onNavigate, onOpenFeedback }: {
  raw: string;
  hasInsufficientData?: boolean;
  onNavigate?: (tab: number, anchor?: string) => void;
  onOpenFeedback?: () => void;
}) {
  // Strip legacy RATING line if present (old cached reviews)
  const body = raw.replace(/RATING:\s*(GREAT|OKAY|POOR)[^\n]*/i, '').trim();
  const noIssues = /no issues found/i.test(body);
  const issues = noIssues ? [] : parseQualityIssues(body);

  const blockers = issues.filter(i => i.tier === 'BLOCKER');
  const suggestions = issues.filter(i => i.tier === 'SUGGESTION');

  // Derived status — never relies on model-stated rating
  // hasInsufficientData overrides to amber when there are no blockers
  const status = blockers.length > 0
    ? { label: 'Needs fixing', color: '#dc2626', dot: '#dc2626' }
    : hasInsufficientData
    ? { label: 'Has gaps', color: '#d97706', dot: '#d97706' }
    : suggestions.length > 2
    ? { label: 'Almost good to go — minor improvements available', color: '#6b7280', dot: '#d97706' }
    : { label: 'Good to go', color: '#16a34a', dot: '#16a34a' };

  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Derived status pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 7, height: 7, borderRadius: '50%',
          background: status.dot, flexShrink: 0,
          display: 'inline-block',
        }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: status.color }}>{status.label}</span>
        {noIssues && !hasInsufficientData && (
          <span style={{ fontSize: 13, color: '#6b7280' }}>— no issues found.</span>
        )}
      </div>

      {/* Has gaps notice — generated text contains [INSUFFICIENT DATA] placeholders */}
      {hasInsufficientData && (
        <div style={{
          padding: '9px 13px', borderRadius: 8,
          background: '#fffbeb', border: '1px solid #fcd34d',
          fontSize: 12, color: '#92400e', lineHeight: 1.55,
        }}>
          ⚠️ Some sections contain <strong>[INSUFFICIENT DATA]</strong> placeholders. Return to the Delivery or Behaviors tab to add notes, then regenerate.
        </div>
      )}

      {/* Tier 1: Blockers */}
      {blockers.length > 0 && (
        <div style={{ borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden', borderLeft: '3px solid #dc2626' }}>
          <div style={{ padding: '8px 14px', background: '#fafafa', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {blockers.length} {blockers.length === 1 ? 'thing' : 'things'} to fix
            </span>
          </div>
          {blockers.map((issue, i) => <IssueRow key={i} issue={issue} onNavigate={onNavigate} onOpenFeedback={onOpenFeedback} />)}
        </div>
      )}

      {/* Tier 2: Suggestions — collapsed by default */}
      {suggestions.length > 0 && (
        <div style={{ borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <button
            onClick={() => setSuggestionsOpen(o => !o)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '9px 14px', background: '#fafafa', border: 'none', cursor: 'pointer',
              fontFamily: 'Inter',
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {suggestions.length} suggestion{suggestions.length > 1 ? 's' : ''} — optional
            </span>
            <span style={{
              fontSize: 10, color: '#9ca3af',
              display: 'inline-block', transition: 'transform 0.15s',
              transform: suggestionsOpen ? 'rotate(180deg)' : 'none',
            }}>▼</span>
          </button>
          {suggestionsOpen && suggestions.map((issue, i) => <IssueRow key={i} issue={issue} onNavigate={onNavigate} onOpenFeedback={onOpenFeedback} />)}
        </div>
      )}
    </div>
  );
}

// ── Consistent field-number chip ──────────────────────────────────────────────

export function FieldNum({ n }: { n: number }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 22, height: 20, borderRadius: 4, padding: '0 5px',
      background: '#f3f4f6', border: '1px solid #e5e7eb',
      fontSize: 11, fontWeight: 700, color: '#6b7280', flexShrink: 0,
    }}>{n}</span>
  );
}

// ── Simple markdown renderer (handles **bold** + line breaks) ─────────────────

function renderMd(text: string): React.ReactNode {
  return text.split('\n').map((line, li) => {
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let k = 0;
    while (remaining) {
      const mm = remaining.match(/^([\s\S]*?)\*\*([\s\S]+?)\*\*([\s\S]*)$/);
      if (!mm) { parts.push(<span key={k++}>{remaining}</span>); break; }
      if (mm[1]) parts.push(<span key={k++}>{mm[1]}</span>);
      parts.push(<strong key={k++}>{mm[2]}</strong>);
      remaining = mm[3];
    }
    return <div key={li} style={{ minHeight: '1.5em', lineHeight: 1.75 }}>{parts}</div>;
  });
}

// ── Culture Amp rating field (display only) ───────────────────────────────────

export function CARatingField({ num, label, rating }: { num: number; label: string; rating: number | null }) {
  const rs = rating ? RATING_SCALE.find(r => r.v === rating) : null;
  return (
    <div style={{
      background: C.card, borderRadius: 10, padding: 20,
      boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: `1px solid ${C.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <FieldNum n={num} />
        <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{label}</span>
      </div>
      {RATING_SCALE.map(r => {
        const selected = rating === r.v;
        const col = getRatingColor(r.v);
        return (
          <div key={r.v} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '10px 12px', borderRadius: 7, marginBottom: 5,
            border: `1.5px solid ${selected ? col : '#e5e7eb'}`,
            background: selected ? col + '12' : '#fafafa',
          }}>
            <div style={{
              width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 2,
              border: `2px solid ${selected ? col : '#d1d5db'}`,
              background: selected ? col : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {selected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: selected ? 700 : 500, color: selected ? col : '#374151' }}>
                {r.v} – {r.label}
              </div>
              {selected && rs && (
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, lineHeight: 1.6 }}>
                  {rs.desc[0]}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {!rating && (
        <p style={{ fontSize: 12, color: '#bbb', fontStyle: 'italic', marginTop: 4 }}>
          Rating not yet entered — go back to the Summary tab to set it.
        </p>
      )}
    </div>
  );
}

// ── Culture Amp text field (preview + editable + copyable) ────────────────────

export function CATextField({
  num, label, hint, value, onEdit, onRegenerate, regenerating, onRefine, refining,
}: {
  num: number; label: string; hint?: string; value: string; onEdit: (v: string) => void;
  onRegenerate?: () => void; regenerating?: boolean;
  onRefine?: (instruction: string) => Promise<{ ok: true } | { ok: false; message: string }>;
  refining?: boolean;
}) {
  const [editMode, setEditMode] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [refineText, setRefineText] = useState('');
  const [refineError, setRefineError] = useState('');

  const copy = () => {
    const clean = value.replace(/\*\*/g, '');
    void navigator.clipboard.writeText(clean).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const busy = refining || regenerating;

  return (
    <div style={{
      background: C.card, borderRadius: 10, padding: 20,
      boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: `1px solid ${C.border}`,
    }}>
      {/* Header — label only, no action buttons */}
      <div style={{ marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FieldNum n={num} />
          <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{label}</span>
        </div>
        {hint && <div style={{ fontSize: 12, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>{hint}</div>}
      </div>

      {/* Text content */}
      {!editMode ? (
        <div style={{
          marginTop: 10, padding: '10px 12px', borderRadius: 8,
          border: `1px solid ${C.border}`, background: '#fafafa',
          fontSize: 13, fontFamily: 'Inter', color: C.text, minHeight: 60,
        }}>
          {value ? renderMd(value) : (
            <span style={{ color: '#bbb', fontStyle: 'italic' }}>Click ✏️ Edit to make changes.</span>
          )}
        </div>
      ) : (
        <textarea
          value={value}
          onChange={e => onEdit(e.target.value)}
          style={{
            width: '100%', minHeight: 200, padding: '10px 12px',
            border: `1px solid ${C.border}`, borderRadius: 8,
            fontSize: 13, lineHeight: 1.75, fontFamily: 'Inter',
            background: '#fafafa', resize: 'vertical', color: C.text,
            outline: 'none', boxSizing: 'border-box', marginTop: 10,
          }}
        />
      )}

      {/* Action bar — directly below text, all actions together */}
      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          onClick={() => setEditMode(m => !m)}
          style={{
            padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'Inter', transition: 'all 0.15s',
            border: `1px solid ${editMode ? C.blue : C.border}`,
            background: editMode ? C.blue + '12' : '#fff',
            color: editMode ? C.blue : C.muted,
          }}
        >{editMode ? '👁 Preview' : '✏️ Edit'}</button>
        {onRefine && (
          <button
            onClick={() => { setRefineOpen(o => !o); setRefineError(''); }}
            style={{
              padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'Inter', transition: 'all 0.15s',
              border: `1px solid ${refineOpen ? C.blue : C.border}`,
              background: refineOpen ? C.blue + '12' : '#fff',
              color: refineOpen ? C.blue : C.muted,
            }}
          >✦ Refine</button>
        )}
        {onRegenerate && (
          <button
            onClick={onRegenerate}
            disabled={busy}
            title="Discard current text and rewrite this field from scratch using your latest notes"
            style={{
              padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'Inter',
              border: `1px solid ${C.border}`, background: '#fff',
              color: busy ? '#bbb' : C.muted,
              display: 'flex', alignItems: 'center', gap: 5,
              opacity: busy ? 0.5 : 1, transition: 'all 0.15s',
            }}
          >
            {regenerating ? (
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                border: '2px solid #d1d5db', borderTopColor: '#6b7280',
                animation: 'spin 0.8s linear infinite', display: 'inline-block',
              }} />
            ) : '↺'} Regenerate
          </button>
        )}
        <button
          onClick={copy}
          style={{
            padding: '5px 14px', borderRadius: 6,
            border: `1px solid ${copied ? '#26bd59' : C.border}`,
            background: copied ? 'rgba(38,189,89,0.08)' : '#fff',
            color: copied ? '#1a7a38' : C.muted,
            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter',
            transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 5,
          }}
        >{copied ? '✓ Copied!' : '📋 Copy'}</button>
      </div>

      {/* Refine panel — immediately below action bar */}
      {refineOpen && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Refine with instruction
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <textarea
              value={refineText}
              onChange={e => { setRefineText(e.target.value); if (refineError) setRefineError(''); }}
              placeholder='Describe what to adjust — e.g. "shorten Win Together", "the Q3 delay was caused by a supplier issue, not the employee"…'
              rows={2}
              disabled={busy}
              style={{
                flex: 1, padding: '8px 10px', borderRadius: 7,
                border: `1px solid ${refineText.trim() ? C.blue : C.border}`,
                background: busy ? '#fafafa' : '#fff',
                fontSize: 12, fontFamily: 'Inter', lineHeight: 1.6, color: C.text,
                resize: 'none', outline: 'none', opacity: busy ? 0.6 : 1,
                transition: 'border-color 0.15s',
              }}
            />
            <button
              onClick={async () => {
                if (!refineText.trim() || busy) return;
                setRefineError('');
                const result = await onRefine!(refineText.trim());
                if (result.ok) {
                  setRefineText('');
                } else {
                  setRefineError(result.message);
                }
              }}
              disabled={!refineText.trim() || busy}
              style={{
                padding: '8px 14px', borderRadius: 7, flexShrink: 0,
                border: `1px solid ${C.blue}`, background: C.blue, color: '#fff',
                fontSize: 12, fontWeight: 600,
                cursor: (!refineText.trim() || busy) ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter', opacity: (!refineText.trim() || busy) ? 0.45 : 1,
                display: 'flex', alignItems: 'center', gap: 6, transition: 'opacity 0.15s',
              }}
            >
              {refining ? (
                <span style={{
                  width: 11, height: 11, borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff',
                  animation: 'spin 0.8s linear infinite', display: 'inline-block',
                }} />
              ) : '↑'} Apply
            </button>
          </div>
          {/* Hard violation — red */}
          {refineError && (
            <div style={{
              marginTop: 8, padding: '8px 12px', borderRadius: 7,
              background: 'rgba(232,41,74,0.06)', border: '1px solid rgba(232,41,74,0.25)',
              display: 'flex', gap: 8, alignItems: 'flex-start',
            }}>
              <span style={{
                fontSize: 10, fontWeight: 800, color: '#9b1c2a',
                background: 'rgba(232,41,74,0.15)', borderRadius: 4,
                padding: '2px 6px', flexShrink: 0, letterSpacing: '0.04em',
                lineHeight: 1.6, marginTop: 1,
              }}>FLAGGED</span>
              <span style={{ fontSize: 12, color: '#9b1c2a', lineHeight: 1.6 }}>{refineError}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Promotion field ───────────────────────────────────────────────────────────

export function CAPromotionField({ promotion }: { promotion: 'yes' | 'no' | null }) {
  return (
    <div style={{
      background: C.card, borderRadius: 10, padding: 20,
      boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: `1px solid ${C.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <FieldNum n={6} />
        <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>I am putting this employee forward for promotion in this cycle.</span>
      </div>
      <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', marginBottom: 10 }}>
        Will not be shared with the employee
      </div>
      {(['yes', 'no'] as const).map(val => {
        const selected = promotion === val;
        return (
          <div key={val} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', borderRadius: 7, marginBottom: 5,
            border: `1.5px solid ${selected ? C.blue : '#e5e7eb'}`,
            background: selected ? C.blue + '12' : '#fafafa',
          }}>
            <div style={{
              width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
              border: `2px solid ${selected ? C.blue : '#d1d5db'}`,
              background: selected ? C.blue : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {selected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
            </div>
            <span style={{ fontSize: 13, fontWeight: selected ? 700 : 400, color: selected ? C.blue : '#374151' }}>
              {val === 'yes' ? 'Yes' : 'No'}
            </span>
          </div>
        );
      })}
      {!promotion && (
        <p style={{ fontSize: 12, color: '#bbb', fontStyle: 'italic', marginTop: 4 }}>
          Promotion decision not yet entered — go back to the Summary tab.
        </p>
      )}
    </div>
  );
}
